// Floating Accessibility widget. A button pinned bottom-left that opens a
// popover of WCAG-aligned preferences. Available on every page.
import { useRef, useState } from "react";
import { Type, EyeOff, Palette, RotateCcw, X } from "lucide-react";
import { useA11y } from "@/contexts/AccessibilityContext";
import { useFocusTrap } from "@/hooks/useFocusTrap";

/**
 * Hand-tuned accessibility figure. Replaces lucide's `<Accessibility>` glyph
 * because at 24px the lucide one reads as a fuzzy blob. This version uses:
 *   - a larger, well-proportioned head circle
 *   - clearly drawn body, arms outstretched (universal a11y stance), legs
 *   - thin enclosing ring for definition against any background
 * Drawn once here so we don't need to ship a custom asset.
 */
function A11yFigure(props) {
  return (
    <svg
      viewBox="0 0 32 32"
      width={props.size || 30}
      height={props.size || 30}
      aria-hidden="true" focusable="false"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Outer ring for definition */}
      <circle cx="16" cy="16" r="14" fill="none" stroke="currentColor" strokeWidth="1.6" opacity="0.5"/>
      {/* Head */}
      <circle cx="16" cy="8" r="2.6" fill="currentColor"/>
      {/* Shoulder + arms outstretched (universal a11y stance) */}
      <path d="M7 14 L25 14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
      {/* Torso */}
      <path d="M16 12 L16 20" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
      {/* Legs */}
      <path d="M16 20 L11 27" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
      <path d="M16 20 L21 27" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
    </svg>
  );
}

export default function AccessibilityWidget() {
  const { prefs, update, reset } = useA11y();
  const [open, setOpen] = useState(false);
  const dialogRef = useRef(null);
  useFocusTrap(dialogRef, { onEscape: () => setOpen(false), enabled: open });

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open accessibility settings"
        title="Accessibility settings"
        data-testid="a11y-widget-btn"
        className="a11y-widget-btn fixed bottom-5 left-5 z-[9999] inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#1F5FA6] text-white shadow-lg ring-2 ring-white/70 hover:bg-[#174b84] hover:scale-105 active:scale-95 transition-transform focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#F2CC8F]"
      >
        <A11yFigure size={30} />
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="a11y-title"
          data-testid="a11y-panel"
          className="fixed inset-0 z-[10000] bg-black/40 flex items-end sm:items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div
            ref={dialogRef}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl"
          >
            <div className="flex items-center gap-2 mb-4">
              <A11yFigure size={20} />
              <h2 id="a11y-title" className="font-display text-xl font-semibold flex-1">Accessibility</h2>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close accessibility panel"
                data-testid="a11y-close-btn"
                className="text-[#5C635E] hover:text-[#2D312E]">
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <p className="text-xs text-[#5C635E] mb-4">
              These settings apply across SpectraLearn and are saved to your device. Your SpectraMind tools remain unchanged.
            </p>

            {/* Dyslexic font */}
            <label className="flex items-start gap-3 py-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!prefs.dyslexicFont}
                onChange={(e) => update({ dyslexicFont: e.target.checked })}
                data-testid="a11y-dyslexic-toggle"
                className="mt-1 h-5 w-5"
              />
              <span className="flex-1">
                <span className="font-medium block">OpenDyslexic font</span>
                <span className="text-xs text-[#5C635E]">Weighted letters that reduce mirroring and rotation.</span>
              </span>
            </label>

            {/* Font size slider */}
            <div className="py-3">
              <div className="flex items-center gap-2 mb-2">
                <Type className="h-4 w-4 text-[#5C635E]" aria-hidden="true" />
                <span className="font-medium">Text size</span>
                <span className="ml-auto text-xs text-[#5C635E]" data-testid="a11y-fontsize-label">{prefs.fontScale}%</span>
              </div>
              <div className="flex gap-2" role="group" aria-label="Text size">
                {[100, 112, 124, 140].map((s) => (
                  <button key={s} type="button" onClick={() => update({ fontScale: s })}
                    aria-pressed={prefs.fontScale === s}
                    data-testid={`a11y-fontsize-${s}`}
                    className={`flex-1 h-10 rounded-xl border text-sm ${prefs.fontScale === s ? "bg-[#1F5FA6] text-white border-[#1F5FA6]" : "bg-white border-[#2D312E]/10 hover:border-[#1F5FA6]/40"}`}>
                    {s === 100 ? "A" : s === 112 ? "A+" : s === 124 ? "A++" : "A+++"}
                  </button>
                ))}
              </div>
            </div>

            {/* Reduce motion */}
            <label className="flex items-start gap-3 py-3 cursor-pointer">
              <input type="checkbox" checked={!!prefs.reduceMotion}
                onChange={(e) => update({ reduceMotion: e.target.checked })}
                data-testid="a11y-reduce-motion-toggle"
                className="mt-1 h-5 w-5" />
              <span className="flex-1">
                <span className="font-medium block">Reduce motion</span>
                <span className="text-xs text-[#5C635E]">Disables animations and transitions.</span>
              </span>
            </label>

            {/* High contrast */}
            <label className="flex items-start gap-3 py-3 cursor-pointer">
              <input type="checkbox" checked={!!prefs.highContrast}
                onChange={(e) => update({ highContrast: e.target.checked })}
                data-testid="a11y-hc-toggle"
                className="mt-1 h-5 w-5" />
              <span className="flex-1">
                <span className="font-medium block">High contrast</span>
                <span className="text-xs text-[#5C635E]">Maximum contrast (AAA). Black background, yellow links.</span>
              </span>
            </label>

            {/* Reading background tint */}
            <div className="py-3">
              <div className="flex items-center gap-2 mb-2">
                <Palette className="h-4 w-4 text-[#5C635E]" aria-hidden="true" />
                <span className="font-medium">Reading background</span>
              </div>
              <div className="flex gap-2" role="group" aria-label="Reading background">
                {[
                  { v: "default", label: "Default" },
                  { v: "cream",   label: "Cream" },
                  { v: "blue",    label: "Blue" },
                ].map((b) => (
                  <button key={b.v} type="button" onClick={() => update({ bgTint: b.v })}
                    aria-pressed={prefs.bgTint === b.v}
                    data-testid={`a11y-bg-${b.v}`}
                    className={`flex-1 h-10 rounded-xl border text-sm ${prefs.bgTint === b.v ? "bg-[#1F5FA6] text-white border-[#1F5FA6]" : "bg-white border-[#2D312E]/10 hover:border-[#1F5FA6]/40"}`}>
                    {b.label}
                  </button>
                ))}
              </div>
            </div>

            <button type="button" onClick={reset}
              data-testid="a11y-reset-btn"
              className="mt-4 inline-flex items-center gap-2 text-sm text-[#5C635E] hover:text-[#1F5FA6]">
              <RotateCcw className="h-4 w-4" aria-hidden="true" /> Reset to defaults
            </button>

            <div className="mt-6 text-xs text-[#7A827C]">
              <EyeOff className="h-3 w-3 inline mr-1" aria-hidden="true" />
              Full accessibility statement at <a className="underline" href="/accessibility">/accessibility</a>.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
